import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, LogOut, RefreshCw, Search, Trash2, Loader2,
  FileCheck, GraduationCap, Building2, Mail, Send,
} from "lucide-react";
import { adminGetSubmissions, adminUpdateStatus, adminDeleteLead } from "@/lib/api";

const TABS = [
  { key: "admissions", label: "Admissions", icon: FileCheck },
  { key: "students", label: "Students", icon: GraduationCap },
  { key: "institutions", label: "Institutions", icon: Building2 },
  { key: "contacts", label: "Contacts", icon: Mail },
  { key: "newsletter", label: "Newsletter", icon: Send },
];

const STATUS_STYLES = {
  new: "border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan",
  contacted: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  closed: "border-white/20 bg-white/5 text-white/50",
};

function rowView(group, r) {
  switch (group) {
    case "admissions":
      return {
        title: r.name,
        sub: `${r.college_name}${r.preferred_course ? ` · ${r.preferred_course}` : ""}${r.intake_year ? ` ${r.intake_year}` : ""}`,
        chips: [r.email, r.mobile, r.city, r.graduation, r.entrance_exam && `${r.entrance_exam} ${r.exam_score || ""}`.trim()],
        note: r.message,
      };
    case "students":
      return {
        title: r.name,
        sub: [r.preferred_course, r.preferred_city].filter(Boolean).join(" · "),
        chips: [r.email, r.mobile, r.city, r.budget && `Budget: ${r.budget}`, r.entrance_exam],
        note: "",
      };
    case "institutions":
      return {
        title: r.institution_name,
        sub: [r.contact_person, r.designation].filter(Boolean).join(" · "),
        chips: [r.email, r.phone, r.city, ...(r.services_required || [])],
        note: r.message,
      };
    case "contacts":
      return { title: r.name, sub: r.subject, chips: [r.email, r.phone], note: r.message };
    default:
      return { title: r.email, sub: "Newsletter subscriber", chips: [], note: "" };
  }
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem("a3-admin-token") || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("admissions");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (tok) => {
    setLoading(true);
    try {
      const d = await adminGetSubmissions(tok);
      setData(d);
      setAuthed(true);
      setToken(tok);
      sessionStorage.setItem("a3-admin-token", tok);
    } catch (e) {
      sessionStorage.removeItem("a3-admin-token");
      setAuthed(false);
      toast.error(e?.response?.status === 401 ? "Invalid admin token" : "Could not load submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    sessionStorage.removeItem("a3-admin-token");
    setToken("");
    setInput("");
    setAuthed(false);
    setData(null);
  };

  const setStatus = async (group, id, status) => {
    const prev = data;
    setData((d) => ({ ...d, [group]: d[group].map((r) => (r.id === id ? { ...r, status } : r)) }));
    try {
      await adminUpdateStatus(token, group, id, status);
    } catch {
      setData(prev);
      toast.error("Could not update status");
    }
  };

  const remove = async (group, id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await adminDeleteLead(token, group, id);
      setData((d) => ({ ...d, [group]: d[group].filter((r) => r.id !== id) }));
      toast.success("Lead deleted");
    } catch {
      toast.error("Could not delete lead");
    }
  };

  const rows = useMemo(() => {
    const list = data?.[tab] || [];
    if (!query.trim()) return list;
    const q = query.toLowerCase();
    return list.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [data, tab, query]);

  if (!authed) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-ink-0 px-5 pt-20" data-testid="admin-login">
        <div className="glass-strong w-full max-w-sm rounded-3xl p-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-cyan/15 text-brand-cyan">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="mt-5 font-display text-2xl font-bold text-white">Admin Access</h1>
          <p className="mt-1 text-sm text-white/50">Enter the A3 admin token to view leads.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (input.trim()) load(input.trim());
            }}
            className="mt-6"
          >
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Admin token"
              className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-white/30 focus:border-brand-cyan/60"
              data-testid="admin-token-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan py-3 text-sm font-semibold text-[#08120A] transition-shadow hover:shadow-[0_0_25px_rgba(198,255,58,0.4)] disabled:opacity-50"
              data-testid="admin-login-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Unlock Dashboard
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-ink-0 px-5 pb-24 pt-28 lg:px-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-cyan">A3 Admin</p>
            <h1 className="mt-2 font-display text-3xl font-bold text-white md:text-4xl">Leads Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(token)} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white transition-colors hover:border-brand-cyan/50" data-testid="admin-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white/70 transition-colors hover:border-red-400/50 hover:text-red-300" data-testid="admin-logout">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
                tab === key ? "border-brand-cyan/50 bg-brand-cyan/10 text-brand-cyan" : "border-white/10 bg-white/[0.03] text-white/60 hover:border-white/25"
              }`}
              data-testid={`admin-tab-${key}`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
              <span className={`rounded-full px-1.5 py-0.5 font-mono text-[10px] ${tab === key ? "bg-brand-cyan/20" : "bg-white/10"}`}>
                {data?.[key]?.length ?? 0}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
          <Search className="h-4 w-4 text-white/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, college..."
            className="w-full bg-transparent text-sm text-white outline-none placeholder:text-white/30"
            data-testid="admin-search"
          />
        </div>

        <div className="mt-6 space-y-3">
          {rows.length === 0 && (
            <div className="rounded-3xl border border-dashed border-white/10 p-12 text-center text-sm text-white/40" data-testid="admin-empty">
              No {tab} found{query ? " for this search" : " yet"}.
            </div>
          )}
          {rows.map((r) => {
            const v = rowView(tab, r);
            const status = r.status || "new";
            return (
              <div key={r.id} className="rounded-2xl border border-white/10 bg-ink-1 p-5 transition-colors hover:border-white/20" data-testid="admin-row">
                <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display text-base font-semibold text-white">{v.title}</h3>
                      <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${STATUS_STYLES[status]}`} data-testid="admin-row-status">
                        {status}
                      </span>
                    </div>
                    {v.sub && <p className="mt-0.5 text-sm text-white/60">{v.sub}</p>}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {v.chips.filter(Boolean).map((c, i) => (
                        <span key={i} className="rounded-full bg-white/5 px-2.5 py-1 font-mono text-[10px] text-white/55">{c}</span>
                      ))}
                    </div>
                    {v.note && <p className="mt-2 line-clamp-2 text-xs italic text-white/40">"{v.note}"</p>}
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-white/30">{fmtDate(r.created_at)}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {tab !== "newsletter" && (
                      <select
                        value={status}
                        onChange={(e) => setStatus(tab, r.id, e.target.value)}
                        className="rounded-full border border-white/15 bg-ink-2 px-3 py-1.5 text-xs text-white outline-none focus:border-brand-cyan/60"
                        data-testid="admin-status-select"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="closed">Closed</option>
                      </select>
                    )}
                    <button
                      onClick={() => remove(tab, r.id)}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-white/40 transition-colors hover:border-red-400/50 hover:text-red-300"
                      data-testid="admin-delete-btn"
                      aria-label="Delete lead"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
}
